'use client';

import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import {
  ARTICLE_HISTORY_QUERY,
  DocumentType,
  type DocumentLine,
  type DocumentLineConnection,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { TYPE_BADGES } from '@paeffgen/react/crm-documents/util';
import { Modal } from './modal';
import { Spinner } from './spinner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PriceHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The document line whose article price history should be shown */
  article: Pick<DocumentLine, 'articleNumber' | 'description'> | null;
}

interface HistoryEntry {
  date: string;
  displayDate: string;
  docNumber: string;
  type: DocumentType;
  quantity: number;
  unitPrice: number;
  unit: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PriceHistoryModal({
  isOpen,
  onClose,
  article,
}: PriceHistoryModalProps) {
  const { data, loading, error } = useQuery<{ articleHistory: DocumentLineConnection }>(ARTICLE_HISTORY_QUERY, {
    variables: {
      input: { articleNumber: article?.articleNumber ?? '' },
      first: 50,
    },
    skip: !isOpen || !article?.articleNumber,
  });

  const history: HistoryEntry[] = useMemo(() => {
    if (!data?.articleHistory?.edges) return [];

    const entries = data.articleHistory.edges
      .filter(
        (edge: { node: DocumentLine & { document?: { type: DocumentType } } }) =>
          edge.node.document?.type !== DocumentType.LIEFERSCHEIN
      )
      .map(
        (edge: { node: DocumentLine & { document?: { documentNumber: string; documentDate: string; type: DocumentType } } }) => {
          const line = edge.node;
          const doc = line.document;
          return {
            date: doc?.documentDate ?? '',
            displayDate: doc?.documentDate
              ? format(new Date(doc.documentDate), 'dd.MM.yyyy', { locale: de })
              : '',
            docNumber: doc?.documentNumber ?? '',
            type: (doc?.type ?? 'RECHNUNG') as DocumentType,
            quantity: line.quantity ?? 0,
            unitPrice: line.unitPrice ?? 0,
            unit: line.unit ?? '',
          };
        }
      );

    return entries.sort(
      (a: HistoryEntry, b: HistoryEntry) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [data]);

  if (!article) return null;

  const currentPrice =
    history.length > 0 ? history[history.length - 1].unitPrice : 0;
  const previousPrice =
    history.length > 1 ? history[history.length - 2].unitPrice : currentPrice;
  const trend =
    currentPrice > previousPrice
      ? 'up'
      : currentPrice < previousPrice
        ? 'down'
        : 'stable';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Preishistorie: ${article.description ?? article.articleNumber}`}
      className="max-w-3xl"
    >
      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="md" />
          <span className="ml-3 text-sm text-gray-500">
            Preishistorie wird geladen...
          </span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          Fehler beim Laden der Preishistorie. Bitte versuchen Sie es
          sp&auml;ter erneut.
        </div>
      )}

      {/* Data state */}
      {!loading && !error && (
        <>
          {/* Current price + trend */}
          <div className="flex items-end gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-500">Aktueller Preis</p>
              <p className="text-3xl font-bold tabular-nums text-gray-900">
                {currentPrice.toLocaleString('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                })}
              </p>
            </div>
            <div
              className={`mb-1 px-2 py-0.5 rounded text-xs font-medium ${
                trend === 'up'
                  ? 'bg-red-50 text-red-600'
                  : trend === 'down'
                    ? 'bg-green-50 text-green-600'
                    : 'bg-gray-100 text-gray-500'
              }`}
            >
              {trend === 'up' && '\u2191 Gestiegen'}
              {trend === 'down' && '\u2193 Gefallen'}
              {trend === 'stable' && '= Stabil'}
            </div>
          </div>

          {/* Chart */}
          {history.length > 0 && (
            <div className="h-64 mb-8 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#e2e8f0"
                  />
                  <XAxis
                    dataKey="displayDate"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    stroke="#cbd5e1"
                    minTickGap={30}
                  />
                  <YAxis
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    stroke="#cbd5e1"
                    unit="\u20AC"
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    }}
                    labelStyle={{
                      fontSize: '12px',
                      color: '#64748b',
                      marginBottom: '4px',
                    }}
                    itemStyle={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#f97316',
                    }}
                    formatter={(value) => [
                      `${Number(value).toLocaleString('de-DE')} \u20AC`,
                      'Preis',
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="unitPrice"
                    stroke="#f97316"
                    strokeWidth={2}
                    dot={{ r: 4, fill: '#f97316', strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Table */}
          {history.length > 0 ? (
            <div className="overflow-x-auto border rounded-lg border-gray-200">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-500 font-medium">
                  <tr>
                    <th className="px-4 py-3">Datum</th>
                    <th className="px-4 py-3">Beleg</th>
                    <th className="px-4 py-3 text-right">Menge</th>
                    <th className="px-4 py-3 text-right">Einzelpreis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history
                    .slice()
                    .reverse()
                    .map((entry, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-600">
                          {entry.displayDate}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wide ${TYPE_BADGES[entry.type] ?? ''}`}
                            >
                              {entry.type.charAt(0)}
                            </span>
                            <span className="font-medium text-gray-900">
                              {entry.docNumber}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {entry.quantity} {entry.unit}
                        </td>
                        <td className="px-4 py-3 text-right font-medium tabular-nums">
                          {entry.unitPrice.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          &euro;
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-8">
              Keine Preishistorie f&uuml;r diesen Artikel vorhanden.
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
